import React, { useMemo } from "react";
import { cn } from "@/lib/utils";
import { Briefcase, TrendingUp, Calendar, Euro, Award } from "lucide-react";

interface WorkforceValueDisplayProps {
  title?: string;
  nettoEinkommen?: number;
  gehaelterProJahr?: number;
  arbeitsjahre?: number;
  animated?: boolean;
  className?: string;
}

export function WorkforceValueDisplay({
  title = "Wert Ihrer Arbeitskraft",
  nettoEinkommen = 2500,
  gehaelterProJahr = 14,
  arbeitsjahre = 30,
  animated = true,
  className,
}: WorkforceValueDisplayProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("de-AT", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(value);
  };

  const calculations = useMemo(() => {
    const jahresgehalt = nettoEinkommen * gehaelterProJahr;
    const gesamtwert = jahresgehalt * arbeitsjahre;
    return { jahresgehalt, gesamtwert };
  }, [nettoEinkommen, gehaelterProJahr, arbeitsjahre]);

  const factors = [
    {
      icon: <Euro className="h-6 w-6" />,
      label: "Nettoeinkommen",
      value: formatCurrency(nettoEinkommen),
      sublabel: "pro Monat",
    },
    {
      icon: <Calendar className="h-6 w-6" />,
      label: "Gehälter",
      value: gehaelterProJahr.toString(),
      sublabel: "pro Jahr",
    },
    {
      icon: <Briefcase className="h-6 w-6" />,
      label: "Arbeitsjahre",
      value: arbeitsjahre.toString(),
      sublabel: "bis zur Pension",
    },
  ];

  return (
    <div className={cn("space-y-6", className)}>
      {title && (
        <div className="text-center">
          <h3 className="font-semibold text-lg">{title}</h3>
          <p className="text-sm text-muted-foreground">
            Was ist Ihre wichtigste Einkommensquelle wert?
          </p>
        </div>
      )}

      {/* Factor cards */}
      <div className="flex items-center justify-center gap-2 flex-wrap">
        {factors.map((factor, idx) => (
          <React.Fragment key={factor.label}>
            <div
              className={cn(
                "text-center p-4 bg-muted/50 rounded-lg min-w-[100px]",
                animated && "animate-in fade-in slide-in-from-bottom-2"
              )}
              style={animated ? { animationDelay: `${idx * 100}ms` } : undefined}
            >
              <div className="flex justify-center text-primary mb-2">
                {factor.icon}
              </div>
              <p className="text-xs text-muted-foreground">{factor.label}</p>
              <p className="text-xl font-bold">{factor.value}</p>
              <p className="text-xs text-muted-foreground">{factor.sublabel}</p>
            </div>
            
            {idx < factors.length - 1 && (
              <span
                className={cn(
                  "text-2xl font-bold text-primary",
                  animated && "animate-in fade-in"
                )}
                style={animated ? { animationDelay: `${idx * 100 + 50}ms` } : undefined}
              >
                ×
              </span>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Result */}
      <div
        className={cn(
          "text-center p-6 bg-gradient-to-br from-primary/20 to-primary/5 rounded-xl border-2 border-primary/30",
          animated && "animate-in fade-in zoom-in-95"
        )}
        style={animated ? { animationDelay: "400ms" } : undefined}
      >
        <div className="flex items-center justify-center gap-2 mb-2">
          <Award className="h-6 w-6 text-primary" />
          <span className="text-lg font-semibold">Ihre Arbeitskraft ist wert:</span>
        </div>
        
        <p className="text-4xl md:text-5xl font-bold text-primary">
          {formatCurrency(calculations.gesamtwert)}
        </p>
        
        <p className="text-sm text-muted-foreground mt-3">
          Jahresgehalt: {formatCurrency(calculations.jahresgehalt)}
        </p>
      </div>

      {/* Protection message */}
      <div className="p-4 bg-amber-50 dark:bg-amber-950/30 rounded-lg">
        <div className="flex items-start gap-3">
          <TrendingUp className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
              Schützen Sie Ihr wertvollstes Gut
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
              Ihre Arbeitskraft ist die Grundlage für alles andere: 
              Lebensstandard, Altersvorsorge, Familie. 
              Eine Berufsunfähigkeitsversicherung sichert diesen Wert ab.
            </p>
          </div>
        </div>
      </div>

      {/* Visual representation */}
      <div className="relative h-4 bg-muted rounded-full overflow-hidden">
        <div
          className={cn(
            "absolute inset-y-0 left-0 bg-gradient-to-r from-primary to-primary/60 rounded-full",
            animated && "animate-in slide-in-from-left"
          )}
          style={{
            width: "100%",
            animationDuration: "1s",
            animationDelay: "500ms",
          }}
        />
      </div>
      
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>Heute</span>
        <span>{arbeitsjahre} Jahre Berufstätigkeit</span>
        <span>Pension</span>
      </div>
    </div>
  );
}
